import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import SupervisorSignatureManager from "../../components/SupervisorSignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import CustomTaskAttachmentSection from "../../components/customTasks/CustomTaskAttachmentSection";
import VehicleAttachmentPanel from "../../components/customTasks/VehicleAttachmentPanel";
import useAccess from "../../hooks/useAccess";
import useCustomTaskOptionSources, {
  getCustomTaskSelectOptions,
} from "../../hooks/useCustomTaskOptionSources";
import {
  isCustomTaskCheckboxChecked,
  resolveCustomTaskFieldValue,
  shouldApplyCustomTaskSaveResult,
} from "../../utils/customTaskFieldState";
import { formatPhoneNumber } from "../../utils/phone";

import "../../styles/Form.css";
import "../../styles/ConsentDocument.css";
import DocumentDownloadButton from "../../components/DocumentDownloadButton";
import logo from "../../images/logo.png";

const CUSTOM_FIELD_TOKEN_REGEX =
  /{{\s*((optional_)?(block_)?(text_field|date_field|textarea_field|yesno_field)):([a-zA-Z0-9_-]+)\s*}}/g;
const LOCKED_POSITION_FIELD_IDS = new Set(["employee_position", "position"]);
const LOCKED_CURRENT_DATE_FIELD_IDS = new Set(["current_date", "effective_date"]);
const LOCKED_HIRE_DATE_FIELD_ID = "date_of_hire";
const DRIVER_CHECK_IN_TASK_KEY = "driver_45_day_check_in";
const DRIVER_CHECK_IN_RENEWAL_PHASE = "renewal";
const PROBATIONARY_REVIEW_TASK_KEY = "probationary_review_90_day";
const DRIVER_LICENCE_COMPLIANCE_TASK_KEY = "driver_s_licence_compliance";
const DRIVER_LICENCE_COMPLIANCE_FIELD_IDS = new Set([
  "driver_licence_number",
  "driver_licence_province",
  "driver_licence_expiry_date",
]);
const ROAD_TEST_TASK_KEY = "road_test";
const CANADA_POST_CLEARANCE_TASK_KEY =
  "canada_post_clearance_screening_documents";
const VEHICLE_TASK_KEY = "vehicle";
const ROAD_TEST_FIELD_IDS = new Set([
  "type_of_test",
  "tester_user_id",
  "tester_other_name",
  "location",
  "test_date",
  "certificate_issued",
]);
const ROAD_TEST_OTHER_TESTER_VALUE = "__other__";
const RECURRING_REVIEW_CONFIG = {
  [DRIVER_CHECK_IN_TASK_KEY]: {
    intervalDays: 45,
    phaseField: "driver_check_in_phase",
  },
  [PROBATIONARY_REVIEW_TASK_KEY]: {
    intervalDays: 90,
    phaseField: "probationary_review_phase",
  },
};

const parseCustomTaskContentMetadata = (html = "") => {
  if (!html || typeof DOMParser === "undefined") {
    return {
      hasHtmlFields: false,
      listColumns: [],
      showListAudit: false,
      optionSources: [],
      attachmentTypes: [],
      requiredAttachmentTypes: [],
      requiredAttachmentTypesByField: null,
      inlineAttachments: false,
      hasEmbeddedExpiryDate: false,
      embeddedExpiryDefaultSource: "",
      embeddedExpiryDefaultField: "",
      expiryLabel: "Expiry Date",
      hasRepeaters: false,
    };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const root = doc.querySelector("[data-custom-task-list-columns]");
  let listColumns = [];
  let attachmentTypes = [];
  let requiredAttachmentTypes = [];
  let requiredAttachmentTypesByField = null;

  try {
    listColumns = JSON.parse(root?.dataset.customTaskListColumns || "[]");
  } catch (error) {
    console.error("Invalid custom task list column configuration", error);
  }

  try {
    attachmentTypes = JSON.parse(
      root?.dataset.customTaskAttachmentTypes || "[]",
    );
  } catch (error) {
    console.error("Invalid custom task attachment type configuration", error);
  }

  try {
    requiredAttachmentTypes = JSON.parse(
      root?.dataset.customTaskRequiredAttachmentTypes || "[]",
    );
  } catch (error) {
    console.error(
      "Invalid custom task required attachment type configuration",
      error,
    );
  }

  try {
    requiredAttachmentTypesByField = JSON.parse(
      root?.dataset.customTaskRequiredAttachmentTypesByField || "null",
    );
  } catch (error) {
    console.error(
      "Invalid conditional custom task attachment configuration",
      error,
    );
  }

  const embeddedExpiryElement = doc.querySelector(
    "[data-custom-task-expiry-date]",
  );
  return {
    hasHtmlFields: Boolean(doc.querySelector("[data-custom-task-field]")),
    listColumns: Array.isArray(listColumns) ? listColumns : [],
    showListAudit: root?.dataset.customTaskListAudit === "true",
    optionSources: Array.from(
      new Set(
        Array.from(doc.querySelectorAll("[data-options-source]"))
          .map((element) => element.dataset.optionsSource)
          .filter(Boolean),
      ),
    ),
    attachmentTypes: Array.isArray(attachmentTypes)
      ? attachmentTypes.filter((value) => String(value || "").trim())
      : [],
    requiredAttachmentTypes: Array.isArray(requiredAttachmentTypes)
      ? requiredAttachmentTypes.filter((value) => String(value || "").trim())
      : [],
    requiredAttachmentTypesByField:
      requiredAttachmentTypesByField &&
      typeof requiredAttachmentTypesByField === "object"
        ? requiredAttachmentTypesByField
        : null,
    inlineAttachments: root?.dataset.customTaskInlineAttachments === "true",
    hasEmbeddedExpiryDate: Boolean(
      embeddedExpiryElement,
    ),
    embeddedExpiryDefaultSource:
      embeddedExpiryElement?.dataset.defaultSource || "",
    embeddedExpiryDefaultField:
      embeddedExpiryElement?.dataset.defaultField || "",
    expiryLabel:
      root?.dataset.customTaskExpiryLabel ||
      embeddedExpiryElement?.dataset.expiryLabel ||
      "Expiry Date",
    hasRepeaters: Boolean(doc.querySelector("[data-custom-task-repeater]")),
  };
};

const syncConditionalCustomTaskFields = (container) => {
  if (!container) return;

  container
    .querySelectorAll("[data-custom-task-thirds-when-field]")
    .forEach((section) => {
      const sourceField = section.dataset.customTaskThirdsWhenField;
      const expectedValue = section.dataset.customTaskThirdsWhenValue || "";
      const source = container.querySelector(
        `[data-custom-task-field="${sourceField}"]`,
      );
      const shouldUseThirds = String(source?.value || "") === expectedValue;

      section.classList.toggle("col-md-6", !shouldUseThirds);
      section.classList.toggle("col-md-4", shouldUseThirds);
    });

  container
    .querySelectorAll("[data-custom-task-visible-when-field]")
    .forEach((section) => {
      const sourceField = section.dataset.customTaskVisibleWhenField;
      const expectedValue = section.dataset.customTaskVisibleWhenValue || "";
      const source = container.querySelector(
        `[data-custom-task-field="${sourceField}"]`,
      );
      const isVisible = String(source?.value || "") === expectedValue;

      section.classList.toggle("d-none", !isVisible);
      section.querySelectorAll("[data-custom-task-field]").forEach((element) => {
        if (!isVisible) element.value = "";
      });
    });
};

const syncRequiredFieldIndicators = (container, { hideRequiredMarkers = false } = {}) => {
  if (!container) return;

  container
    .querySelectorAll(".custom-task-required-marker")
    .forEach((marker) => marker.remove());

  if (hideRequiredMarkers) return;

  const findFieldLabel = (element) => {
    if (element.id) {
      const matchingLabel = Array.from(container.querySelectorAll("label[for]")).find(
        (label) => label.htmlFor === element.id,
      );
      if (matchingLabel) return matchingLabel;
    }

    if (!element.closest("[data-custom-task-yesno-group]")) {
      const wrappingLabel = element.closest("label");
      if (wrappingLabel) return wrappingLabel;
    }

    let parent = element.parentElement;
    while (parent && parent !== container) {
      const directLabel = Array.from(parent.children).find(
        (child) => child.tagName === "LABEL",
      );
      if (directLabel) return directLabel;
      parent = parent.parentElement;
    }

    return null;
  };

  container.querySelectorAll("[data-custom-task-field]").forEach((element) => {
    const conditionalSourceId = element.dataset.requiredWhenField;
    const conditionalSource = conditionalSourceId
      ? Array.from(container.querySelectorAll("[data-custom-task-field]")).find(
          (field) => field.dataset.customTaskField === conditionalSourceId,
        )
      : null;
    const conditionMatches =
      Boolean(conditionalSourceId) &&
      String(conditionalSource?.value || "") ===
        String(element.dataset.requiredWhenValue || "");
    const isRequired =
      !element.disabled &&
      (element.required || element.dataset.required === "true" || conditionMatches);

    if (!isRequired) return;

    const label = findFieldLabel(element);
    if (!label || label.querySelector(".custom-task-required-marker")) return;

    const existingMarker = Array.from(label.querySelectorAll("span")).find(
      (span) => span.textContent.trim() === "*",
    );
    if (existingMarker) {
      existingMarker.classList.add(
        "text-danger",
        "custom-task-required-marker",
        "ms-1",
      );
      existingMarker.setAttribute("aria-hidden", "true");
      return;
    }

    if (label.textContent.trim().endsWith("*")) return;

    const marker = document.createElement("span");
    marker.className = "text-danger custom-task-required-marker ms-1";
    marker.setAttribute("aria-hidden", "true");
    marker.textContent = "*";
    label.appendChild(marker);
  });
};

const getDriverLicenseRows = (payload) =>
  Array.isArray(payload) ? payload : payload ? [payload] : [];

const isPrimaryDriverLicense = (row) =>
  String(row?.dl_status || row?.license_type || row?.licenseType || "")
    .toLowerCase()
    .includes("primary");

const isActiveDriverLicense = (row) =>
  String(row?.dl_driver_status || row?.driver_status || row?.driverStatus || "")
    .toLowerCase()
    .includes("active");

const getLatestDriverLicense = (payload) => {
  const rows = getDriverLicenseRows(payload);
  return (
    [...rows]
      .filter(
        (row) => isActiveDriverLicense(row) && isPrimaryDriverLicense(row),
      )
      .sort((a, b) => Number(b?.dl_id || 0) - Number(a?.dl_id || 0))[0] || {}
  );
};

const getDriverLicenseDefaultValue = (license, source) => {
  switch (source) {
    case "latest-driver-license":
    case "latest-driver-license-number":
      return license?.dl_license_no || "";
    case "latest-driver-license-province":
      return license?.dl_province || "";
    case "latest-driver-license-expiry":
      return formatDateInputValue(license?.dl_expdate);
    default:
      return "";
  }
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const getCustomFieldTokens = (html = "") => {
  const fields = [];
  const seen = new Set();
  let match;

  CUSTOM_FIELD_TOKEN_REGEX.lastIndex = 0;
  while ((match = CUSTOM_FIELD_TOKEN_REGEX.exec(html))) {
    const id = match[5];
    if (seen.has(id)) continue;

    seen.add(id);
    fields.push({
      id,
      type:
        match[4] === "date_field"
          ? "date"
          : match[4] === "textarea_field"
            ? "textarea"
            : match[4] === "yesno_field"
              ? "yesno"
              : "text",
      required: !match[2],
      isBlock: Boolean(match[3]),
    });
  }

  return fields;
};

const formatDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => {
  const normalized = formatDateInputValue(value);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-");
  return `${month}/${day}/${year}`;
};

const formatTodayInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatOneYearFromTodayInputValue = () => {
  return addYearsToDateValue(formatTodayInputValue(), 1);
};

const addYearsToDateValue = (value, years) => {
  const normalized = formatDateInputValue(value);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-").map(Number);
  const targetYear = year + Number(years || 0);
  const lastDayOfTargetMonth = new Date(targetYear, month, 0).getDate();
  return `${targetYear}-${String(month).padStart(2, "0")}-${String(
    Math.min(day, lastDayOfTargetMonth),
  ).padStart(2, "0")}`;
};

const sortTaskRecordsNewestFirst = (records = []) => {
  const getExpiryTime = (record) => {
    const value = record?.expiry_date;
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  };
  const hasExpiryDates = records.some(
    (record) => getExpiryTime(record) !== null,
  );

  return [...records].sort((a, b) => {
    if (hasExpiryDates) {
      const aExpiry = getExpiryTime(a);
      const bExpiry = getExpiryTime(b);
      if (aExpiry !== bExpiry) {
        if (aExpiry === null) return 1;
        if (bExpiry === null) return -1;
        return bExpiry - aExpiry;
      }
    }

    return (
      Number(b?.sort_id || b?.record_id || b?.reference_id || 0) -
      Number(a?.sort_id || a?.record_id || a?.reference_id || 0)
    );
  });
};

const getLockedFieldDefaultValue = (fieldId, user, todayInputValue) => {
  if (LOCKED_POSITION_FIELD_IDS.has(fieldId)) {
    return user?.position || "";
  }

  if (LOCKED_CURRENT_DATE_FIELD_IDS.has(fieldId)) {
    return todayInputValue;
  }

  if (fieldId === LOCKED_HIRE_DATE_FIELD_ID) {
    return formatDateInputValue(user?.user_hiring_date || user?.date_hired);
  }

  return "";
};

const isLockedFieldForUser = (fieldId, isAdmin) =>
  !isAdmin &&
  [
    ...LOCKED_POSITION_FIELD_IDS,
    ...LOCKED_CURRENT_DATE_FIELD_IDS,
    LOCKED_HIRE_DATE_FIELD_ID,
  ].includes(fieldId);

const isCustomTaskPhoneField = (element, fieldId = "") => {
  const inputType = String(element?.type || "").toLowerCase();
  if (inputType === "tel") return true;
  if (inputType && inputType !== "text") return false;

  return [fieldId, element?.name].some((identifier) =>
    /(?:^|[_-])(?:phone|mobile)(?:_number)?$/i.test(String(identifier || "")),
  );
};

const makeRoadTestFieldsOptional = (html = "") =>
  String(html)
    .replace(
      /<span\b[^>]*class=(["'])[^"']*\btext-danger\b[^"']*\1[^>]*>\s*\*\s*<\/span>/gi,
      "",
    )
    .replace(
      /\sdata-required(?:-when-field|-when-value)?=(["'])[^"']*\1/gi,
      "",
    )
    .replace(
      /\srequired(?:=(["'])(?:required|true)\1)?(?=\s|\/?>)/gi,
      "",
    );

const normalizeTaskFlags = (task) => {
  if (!task) return task;

  return {
    ...task,
    t_content:
      task.t_key === ROAD_TEST_TASK_KEY
        ? makeRoadTestFieldsOptional(task.t_content)
        : task.t_content,
    t_allow_upload: Number(task.t_allow_upload ?? 0),
    t_need_complete: Number(task.t_need_complete ?? 1),
    t_show_list: Number(task.t_show_list ?? 0),
    t_show_expiry: Number(task.t_show_expiry ?? 0),
    t_show_issue_date: Number(task.t_show_issue_date ?? 0),
    t_show_document_number: Number(task.t_show_document_number ?? 0),
    t_show_card_number: Number(task.t_show_card_number ?? 0),
    t_show_issue_country: Number(task.t_show_issue_country ?? 0),
    t_show_nationality: Number(task.t_show_nationality ?? 0),
    t_show_signature: Number(task.t_show_signature ?? 1),
    t_requires_supervisor_signature: Number(
      task.t_requires_supervisor_signature ?? 0,
    ),
    t_show_employee: Number(task.t_show_employee ?? 1),
    t_show_date: Number(task.t_show_date ?? 1),
  };
};

const CustomTaskPage = () => {
  const { t_key, userId: paramUserId, recordId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const { hasPermission } = useAccess();

  const loggedInUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  let isSuperAdmin = false;
  try {
    const currentUser = JSON.parse(localStorage.getItem("me") || "{}");
    const roles = typeof currentUser.emp_roles === "string"
      ? JSON.parse(currentUser.emp_roles || "[]") : currentUser.emp_roles;
    isSuperAdmin = isAdmin && Array.isArray(roles) && roles.map(Number).includes(17);
  } catch {
    isSuperAdmin = false;
  }

  const effectiveUserId = isAdmin && paramUserId ? paramUserId : loggedInUserId;
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");
  const isSlidePage = location.pathname.startsWith("/slide/");
  const listViewRequested =
    new URLSearchParams(location.search).get("view") === "list";

  const [task, setTask] = useState(null);
  const [user, setUser] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [issueCountry, setIssueCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [countries, setCountries] = useState([]);
  const [taskStatusValue, setTaskStatusValue] = useState("not_completed");
  const [taskSignedDate, setTaskSignedDate] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedAttachmentType, setSelectedAttachmentType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [inlineAttachmentHosts, setInlineAttachmentHosts] = useState([]);
  const [isSavingFiles, setIsSavingFiles] = useState(false);
  const [isDeletingAttachmentId, setIsDeletingAttachmentId] = useState(null);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [hasSupervisorSignature, setHasSupervisorSignature] = useState(false);
  const [latestDriverLicense, setLatestDriverLicense] = useState({});
  const [ownerOperatorCompanyProfile, setOwnerOperatorCompanyProfile] = useState({});
  const [records, setRecords] = useState([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [hasResolvedRecords, setHasResolvedRecords] = useState(false);
  const [recordLoadFailed, setRecordLoadFailed] = useState(false);
  const [taskPdfUrl, setTaskPdfUrl] = useState("");
  const taskContentRef = useRef(null);
  const documentRef = useRef(null);
  const customFieldValuesRef = useRef({});
  const customFieldSaveTimerRef = useRef(null);
  const customFieldSaveQueueRef = useRef(Promise.resolve());
  const customFieldLoadRequestRef = useRef(0);
  const customFieldEditVersionRef = useRef(0);
  const vehicleUploadInFlightRef = useRef(false);
  const taskLoadRequestRef = useRef(0);
  const taskRecordsRequestRef = useRef(0);
  const currentReferenceId =
    recordId && recordId !== "new" && recordId !== "single" ? recordId : null;
  const isNewListRecord =
    recordId === "new" || location.pathname.endsWith("/new");
  const shouldShowListView =
    task?.t_show_list === 1 && !recordId && !isNewListRecord && !isSlidePage;
  const shouldAutoOpenLatestRecord = shouldShowListView && !listViewRequested;
  const shouldRenderListView =
    shouldShowListView && (listViewRequested || (!isLoadingRecords && records.length === 0));
  const shouldHoldFormRenderForAutoOpen =
    shouldAutoOpenLatestRecord && isLoadingRecords;
  const recurringReviewConfig = RECURRING_REVIEW_CONFIG[task?.t_key] || null;
  const isBackgroundCheckConsent =
    /consent to background checks?/i.test(task?.t_display_title || "") ||
    ["backgroundcheck", "background_check"].includes(task?.t_key);
  const isPspDisclosure =
    task?.t_key === "psp_disclosure_and_authorization";
  const isRecurringReviewTask = Boolean(recurringReviewConfig);
  const isItemRecordTask = [
    "record_of_supplied_items",
    "record_of_returned_items",
  ].includes(task?.t_key);
  const isRecurringExpiryCleared = [true, 1, "true", "1"].includes(customFieldValues?.expiry_cleared);
  const isRecurringRenewalPhase =
    String(customFieldValues?.[recurringReviewConfig?.phaseField] || "").toLowerCase() ===
    DRIVER_CHECK_IN_RENEWAL_PHASE;
  const isReviewed =
    taskStatusValue === "completed" || taskStatusValue === "almost_expired";
  const isReadyForReview = taskStatusValue === "review";
  const hasReachedRecurringExpiry =
    isRecurringReviewTask &&
    Boolean(formatDateInputValue(expiryDate)) &&
    formatDateInputValue(expiryDate) <= formatTodayInputValue();
  const requiredRecurringReviewFields = useMemo(
    () =>
      getCustomFieldTokens(task?.t_content || "").filter(
        (field) => field.type === "yesno",
      ),
    [task?.t_content],
  );
  const hasSavedRecurringReviewAnswers =
    requiredRecurringReviewFields.length > 0 &&
    requiredRecurringReviewFields.every((field) =>
      String(customFieldValues?.[field.id] || "").trim(),
    );
  const shouldShowRecurringSignature =
    hasSavedRecurringReviewAnswers;
  const shouldShowRecurringEmployeeSignature =
    shouldShowRecurringSignature && hasSupervisorSignature;

  useEffect(() => {
    setHasSupervisorSignature(false);
  }, [effectiveUserId, task?.t_id, currentReferenceId]);
  const contentMetadata = useMemo(
    () => parseCustomTaskContentMetadata(task?.t_content || ""),
    [task?.t_content],
  );

  useEffect(() => {
    if (task?.t_key !== VEHICLE_TASK_KEY || shouldShowListView) {
      setInlineAttachmentHosts([]);
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const hosts = Array.from(
        taskContentRef.current?.querySelectorAll(
          "[data-vehicle-attachment-panel]",
        ) || [],
      ).map((element) => ({
        element,
        attachmentType: element.dataset.vehicleAttachmentPanel,
        multiple: element.dataset.vehicleAttachmentMultiple === "true",
      }));
      setInlineAttachmentHosts(hosts);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    currentReferenceId,
    shouldShowListView,
    task?.t_content,
    task?.t_key,
    user,
  ]);
  const attachmentTypeRule = contentMetadata.requiredAttachmentTypesByField;
  const attachmentTypeRuleValue = String(
    customFieldValues?.[attachmentTypeRule?.field] || "",
  );
  const conditionalAttachmentTypes =
    attachmentTypeRule?.values?.[attachmentTypeRuleValue];
  const effectiveAttachmentTypes = Array.isArray(conditionalAttachmentTypes)
    ? conditionalAttachmentTypes
    : contentMetadata.attachmentTypes;
  const optionSourceRows = useCustomTaskOptionSources({
    apiBase: API_BASE,
    token,
    userId: effectiveUserId,
    sources: contentMetadata.optionSources,
  });
  const formatCustomTaskListColumnValue = (record, column) => {
    const value = record.fields?.[column.field];

    if (
      task?.t_key === ROAD_TEST_TASK_KEY &&
      column.field === "tester_user_id"
    ) {
      if (String(value || "") === ROAD_TEST_OTHER_TESTER_VALUE) {
        return record.fields?.tester_other_name || "-";
      }

      const tester = getCustomTaskSelectOptions(
        "road-testers",
        optionSourceRows["road-testers"] || [],
      ).find((option) => option.value === String(value || ""));
      return tester?.label || value || "-";
    }

    return value || "-";
  };

  const referenceParams = useMemo(
    () =>
      currentReferenceId !== null ? { referenceId: currentReferenceId } : {},
    [currentReferenceId],
  );

  const refreshTaskStatus = useCallback((referenceIdOverride = currentReferenceId) => {
    if (isNewListRecord) {
      setTaskStatusValue("not_completed");
      setTaskSignedDate("");
      return Promise.resolve();
    }

    if (!task?.t_id || !effectiveUserId) return Promise.resolve();

    return axios
      .get(`${API_BASE}/api/usertasks/status/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const idKey = String(task.t_id);
        const keyKey = String(task.t_key || "");
        const status =
          (referenceIdOverride !== null
            ? res.data?.workflowStatus?.[`${idKey}_${referenceIdOverride}`]
            : undefined) ??
          (referenceIdOverride !== null
            ? res.data?.workflowStatus?.[`${keyKey}_${referenceIdOverride}`]
            : undefined) ??
          (referenceIdOverride !== null
            ? res.data?.status?.[`${idKey}_${referenceIdOverride}`]
            : undefined) ??
          (referenceIdOverride !== null
            ? res.data?.status?.[`${keyKey}_${referenceIdOverride}`]
            : undefined) ??
          res.data?.workflowStatus?.[idKey] ??
          res.data?.workflowStatus?.[keyKey] ??
          res.data?.status?.[idKey] ??
          res.data?.status?.[keyKey] ??
          "not_completed";
        const signedDate =
          (referenceIdOverride !== null
            ? res.data?.signdates?.[`${idKey}_${referenceIdOverride}`]
            : undefined) ??
          (referenceIdOverride !== null
            ? res.data?.signdates?.[`${keyKey}_${referenceIdOverride}`]
            : undefined) ??
          res.data?.signdates?.[idKey] ??
          res.data?.signdates?.[keyKey] ??
          "";
        setTaskStatusValue(status);
        setTaskSignedDate(signedDate);
      })
      .catch((err) => {
        console.error("Failed to load custom task status", err);
      });
  }, [API_BASE, currentReferenceId, effectiveUserId, isNewListRecord, task, token]);

  useEffect(() => {
    if (!isNewListRecord) return;

    setTaskStatusValue("not_completed");
    setTaskSignedDate("");
    setUploadedFiles([]);
    setPendingFiles([]);
    setSelectedAttachmentType("");
    setExpiryDate("");
    setIssueDate("");
    setDocumentNumber("");
    setCardNumber("");
    setIssueCountry("");
    setNationality("");
    setCustomFieldValues({});
    customFieldValuesRef.current = {};
  }, [isNewListRecord, t_key, effectiveUserId]);

  // --------------------------------------------------
  // Load Custom Task
  // --------------------------------------------------
  useEffect(() => {
    const requestId = taskLoadRequestRef.current + 1;
    taskLoadRequestRef.current = requestId;

    axios
      .get(`${API_BASE}/api/custom-tasks/by-key/${t_key}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (taskLoadRequestRef.current !== requestId) return;
        setTask(normalizeTaskFlags(res.data));
      })
      .catch((error) => {
        if (taskLoadRequestRef.current !== requestId) return;

        if (error?.response?.status === 404) {
          alert("Task not found");
          navigate("/userhome");
          return;
        }

        console.error("Failed to load custom task:", error);
      });
  }, [API_BASE, t_key, token, navigate]);

  // --------------------------------------------------
  // Load User (Employee Name)
  // --------------------------------------------------
  useEffect(() => {
    if (!effectiveUserId) return;

    axios
      .get(`${API_BASE}/api/users/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => console.error("Failed to load user"));
  }, [API_BASE, effectiveUserId, token]);

  useEffect(() => {
    const needsCompanyDefaults = task?.t_content?.includes(
      'data-default-source="owner-operator-',
    );
    if (!effectiveUserId || !needsCompanyDefaults) {
      setOwnerOperatorCompanyProfile({});
      return;
    }

    axios
      .get(`${API_BASE}/api/owner-operator-company/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setOwnerOperatorCompanyProfile(response.data || {}))
      .catch(() => setOwnerOperatorCompanyProfile({}));
  }, [API_BASE, effectiveUserId, task?.t_content, token]);

  useEffect(() => {
    if (!effectiveUserId) return;

    if (task?.t_content?.includes('data-default-source="latest-driver-license')) {
      axios
        .get(`${API_BASE}/api/driverlicense/${effectiveUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setLatestDriverLicense(getLatestDriverLicense(res.data));
        })
        .catch((error) => {
          console.error("Failed to load latest driver license", error);
          setLatestDriverLicense({});
        });
    } else {
      setLatestDriverLicense({});
    }
  }, [
    API_BASE,
    contentMetadata.optionSources,
    effectiveUserId,
    task?.t_content,
    token,
  ]);

  useEffect(() => {
    const defaultSource = contentMetadata.embeddedExpiryDefaultSource;
    if (
      shouldShowListView ||
      expiryDate ||
      !contentMetadata.hasEmbeddedExpiryDate ||
      !["today-plus-one-year", "field-plus-one-year"].includes(defaultSource)
    ) {
      return;
    }

    if (defaultSource === "today-plus-one-year") {
      setExpiryDate(formatOneYearFromTodayInputValue());
      return;
    }

    const defaultFieldId = contentMetadata.embeddedExpiryDefaultField;
    const defaultFieldElement = defaultFieldId
      ? taskContentRef.current?.querySelector(
          `[data-custom-task-field="${defaultFieldId}"]`,
        )
      : null;
    const sourceDate =
      customFieldValuesRef.current[defaultFieldId] ||
      defaultFieldElement?.value ||
      (defaultFieldElement?.dataset.defaultSource === "today"
        ? formatTodayInputValue()
        : "");
    const derivedDate = addYearsToDateValue(sourceDate, 1);
    if (derivedDate) setExpiryDate(derivedDate);
  }, [
    contentMetadata.embeddedExpiryDefaultField,
    contentMetadata.embeddedExpiryDefaultSource,
    contentMetadata.hasEmbeddedExpiryDate,
    expiryDate,
    shouldShowListView,
  ]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/dropdowns/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCountries(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Failed to load countries", err);
      });
  }, [API_BASE, token]);

  useEffect(() => {
    if (
      !task?.t_allow_upload ||
      !effectiveUserId ||
      isNewListRecord ||
      shouldShowListView
    ) {
      return;
    }

    axios
      .get(
        `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: referenceParams,
        },
      )
      .then((res) => {
        setUploadedFiles(Array.isArray(res.data) ? res.data : []);
        refreshTaskStatus();
      })
      .catch((err) => {
        console.error("Failed to load custom task attachments", err);
      });
  }, [
    API_BASE,
    currentReferenceId,
    effectiveUserId,
    isNewListRecord,
    referenceParams,
    shouldShowListView,
    task,
    token,
    refreshTaskStatus,
  ]);

  useEffect(() => {
    refreshTaskStatus();
  }, [refreshTaskStatus]);

  const loadTaskRecords = useCallback(() => {
    if (!shouldShowListView || !effectiveUserId || !task?.t_key) {
      return Promise.resolve();
    }

    const requestId = taskRecordsRequestRef.current + 1;
    taskRecordsRequestRef.current = requestId;
    setIsLoadingRecords(true);
    setHasResolvedRecords(false);
    setRecordLoadFailed(false);

    return axios
      .get(
        `${API_BASE}/api/custom-tasks/records/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        if (taskRecordsRequestRef.current !== requestId) return;

        const taskRecords = Array.isArray(res.data) ? res.data : [];
        setRecords(sortTaskRecordsNewestFirst(taskRecords));
        setHasResolvedRecords(true);
      })
      .catch((err) => {
        if (taskRecordsRequestRef.current !== requestId) return;

        console.error("Failed to load custom task records", err);
        setRecordLoadFailed(true);
        setHasResolvedRecords(true);
      })
      .finally(() => {
        if (taskRecordsRequestRef.current !== requestId) return;
        setIsLoadingRecords(false);
      });
  }, [API_BASE, effectiveUserId, shouldShowListView, task?.t_key, token]);

  useEffect(() => {
    if (!shouldShowListView) {
      setRecords([]);
      setIsLoadingRecords(false);
      setHasResolvedRecords(false);
      setRecordLoadFailed(false);
      return;
    }

    loadTaskRecords();
  }, [loadTaskRecords, location.key, shouldShowListView]);

  useEffect(() => {
    if (
      !shouldAutoOpenLatestRecord ||
      !hasResolvedRecords ||
      recordLoadFailed ||
      isLoadingRecords ||
      !records.length ||
      !task?.t_key ||
      !effectiveUserId
    ) {
      return;
    }

    const latestRecord = records[0];

    if (!latestRecord?.record_id) return;

    navigate(
      `/task/${task.t_key}/${effectiveUserId}/detail/${latestRecord.record_id}`,
      { replace: true },
    );
  }, [
    effectiveUserId,
    hasResolvedRecords,
    isLoadingRecords,
    navigate,
    recordLoadFailed,
    records,
    shouldAutoOpenLatestRecord,
    task?.t_key,
  ]);

  useEffect(() => {
    if (
      !shouldAutoOpenLatestRecord ||
      !hasResolvedRecords ||
      recordLoadFailed ||
      isLoadingRecords ||
      records.length > 0 ||
      !task?.t_key ||
      !effectiveUserId
    ) {
      return;
    }

    navigate(`/task/${task.t_key}/${effectiveUserId}?view=list`, {
      replace: true,
    });
  }, [
    effectiveUserId,
    hasResolvedRecords,
    isLoadingRecords,
    navigate,
    recordLoadFailed,
    records.length,
    shouldAutoOpenLatestRecord,
    task?.t_key,
  ]);

  useEffect(() => {
    if (!shouldShowListView) return undefined;

    const refreshTaskList = () => {
      loadTaskRecords();
    };

    window.addEventListener("focus", refreshTaskList);
    window.addEventListener("pageshow", refreshTaskList);

    return () => {
      window.removeEventListener("focus", refreshTaskList);
      window.removeEventListener("pageshow", refreshTaskList);
    };
  }, [loadTaskRecords, shouldShowListView]);

  useEffect(() => {
    return () => {
      if (customFieldSaveTimerRef.current) {
        clearTimeout(customFieldSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!task?.t_pdf) {
      setTaskPdfUrl("");
      return undefined;
    }

    let isActive = true;
    let objectUrl = "";

    axios
      .get(`${API_BASE}/api/custom-tasks/pdf/by-key/${encodeURIComponent(t_key)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      })
      .then((res) => {
        if (!isActive) return;
        objectUrl = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" }),
        );
        setTaskPdfUrl(objectUrl);
      })
      .catch((err) => {
        console.error("Failed to load task PDF", err);
        if (isActive) {
          setTaskPdfUrl("");
        }
      });

    return () => {
      isActive = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [API_BASE, t_key, task?.t_pdf, token]);

  useEffect(() => {
    const requestId = customFieldLoadRequestRef.current + 1;
    customFieldLoadRequestRef.current = requestId;
    const editVersionAtRequest = customFieldEditVersionRef.current;

    if (
      !task?.t_key ||
      !effectiveUserId ||
      isNewListRecord ||
      shouldShowListView
    ) {
      return;
    }
    if (
      !getCustomFieldTokens(task.t_content).length &&
      task.t_show_document_number !== 1 &&
      task.t_show_card_number !== 1 &&
      task.t_show_issue_country !== 1 &&
      task.t_show_nationality !== 1 &&
      !contentMetadata.hasHtmlFields &&
      !contentMetadata.hasRepeaters
    ) {
      return;
    }

    axios
      .get(
        `${API_BASE}/api/custom-tasks/fields/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: referenceParams,
        },
      )
      .then((res) => {
        if (customFieldLoadRequestRef.current !== requestId) return;

        const loadedFields = res.data?.fields || {};
        const fields =
          customFieldEditVersionRef.current !== editVersionAtRequest
            ? { ...loadedFields, ...customFieldValuesRef.current }
            : loadedFields;
        customFieldValuesRef.current = fields;
        setCustomFieldValues(fields);
        if (isRecurringReviewTask && [true, 1, "true", "1"].includes(fields.expiry_cleared)) setExpiryDate("");
        setDocumentNumber(
          fields.document_number ?? fields.documentNumber ?? "",
        );
        setCardNumber(
          fields.card_number ??
            fields.cardNumber ??
            fields.fuel_card_number ??
            "",
        );
        setIssueCountry(fields.issue_country ?? fields.issueCountry ?? "");
        setNationality(fields.nationality ?? "");
      })
      .catch((err) => {
        if (customFieldLoadRequestRef.current !== requestId) return;
        console.error("Failed to load custom task fields", err);
      });
  }, [
    API_BASE,
    currentReferenceId,
    contentMetadata.hasHtmlFields,
    contentMetadata.hasRepeaters,
    effectiveUserId,
    isRecurringReviewTask,
    isNewListRecord,
    referenceParams,
    shouldShowListView,
    task?.t_content,
    task?.t_key,
    task?.t_show_card_number,
    task?.t_show_document_number,
    task?.t_show_issue_country,
    task?.t_show_nationality,
    taskStatusValue,
    token,
  ]);

  useEffect(() => {
    const container = taskContentRef.current;
    if (!container || !user) return;

    const resolveDefaultValue = (element) => {
      switch (element.dataset.defaultSource) {
        case "user-employee-id":
          return user.userid_no || "";
        case "user-full-name":
          return [user.first_name, user.last_name].filter(Boolean).join(" ");
        case "user-first-name":
          return user.first_name || "";
        case "user-last-name":
          return user.last_name || "";
        case "user-hiring-date":
          return formatDateInputValue(user.user_hiring_date || user.date_hired);
        case "user-org-unit":
          return user.org_unit_name || user.org_unit || "";
        case "owner-operator-company-name":
          return ownerOperatorCompanyProfile.companyName || "";
        case "owner-operator-business-address":
          return [
            ownerOperatorCompanyProfile.address1,
            ownerOperatorCompanyProfile.address2,
            ownerOperatorCompanyProfile.city,
            ownerOperatorCompanyProfile.province,
            ownerOperatorCompanyProfile.postalCode,
          ]
            .filter(Boolean)
            .join(", ");
        case "latest-driver-license":
        case "latest-driver-license-number":
        case "latest-driver-license-province":
        case "latest-driver-license-expiry":
          return getDriverLicenseDefaultValue(
            latestDriverLicense,
            element.dataset.defaultSource,
          );
        case "today":
          return formatTodayInputValue();
        default:
          return element.dataset.defaultValue || "";
      }
    };

    container.querySelectorAll("[data-options-source]").forEach((select) => {
      const placeholder = select.dataset.placeholder || "Select";
      const selectedValue =
        customFieldValuesRef.current[select.dataset.customTaskField] || "";
      const source = select.dataset.optionsSource;
      const options = getCustomTaskSelectOptions(
        source,
        optionSourceRows[source] || [],
      );

      select.innerHTML = "";
      select.add(new Option(placeholder, ""));
      options.forEach((option) => {
        select.add(new Option(option.label, option.value));
      });
      select.value = selectedValue;
    });

    container.querySelectorAll("[data-custom-task-field]").forEach((element) => {
      const fieldId = element.dataset.customTaskField;
      const savedValue = customFieldValuesRef.current[fieldId];
      const defaultValue = resolveDefaultValue(element);
      const value = resolveCustomTaskFieldValue(
        savedValue,
        defaultValue,
        element.type,
      );

      if (element.type === "radio") {
        element.name = `custom-task-${fieldId}`;
        element.checked = String(element.value) === String(value);
      } else if (element.type === "checkbox") {
        element.checked = isCustomTaskCheckboxChecked(value);
      } else {
        element.value = value;
      }

      element.disabled =
        isReviewed ||
        (element.dataset.adminOnly === "true" && !isAdmin) ||
        (isRecurringReviewTask && !isAdmin) ||
        (isRecurringRenewalPhase && element.hasAttribute("data-custom-task-expiry-date"));
    });

    syncConditionalCustomTaskFields(container);

    container
      .querySelectorAll("[data-custom-task-expiry-date]")
      .forEach((element) => {
        element.value = expiryDate;
        element.disabled =
          isReviewed ||
          (isRecurringReviewTask && !isAdmin) ||
          isRecurringRenewalPhase;
        const directContainer = element.parentElement;
        const expiryContainer = directContainer?.querySelector("label")
          ? directContainer
          : directContainer?.parentElement;
        expiryContainer?.classList.add("d-none");
      });

    container.querySelectorAll("[data-custom-task-repeater]").forEach((repeater) => {
      const repeaterId = repeater.dataset.customTaskRepeater;
      const itemsContainer = repeater.querySelector("[data-repeater-items]");
      const template = repeater.querySelector("template[data-repeater-template]");
      if (!itemsContainer || !template) return;

      const savedItems = customFieldValuesRef.current[repeaterId];
      const defaultItemCount = Number(repeater.dataset.repeaterDefaultItems || 0);
      const items = Array.isArray(savedItems)
        ? savedItems
        : Array.from({ length: defaultItemCount }, () => ({}));

      itemsContainer.innerHTML = "";
      items.forEach((item) => {
        const fragment = template.content.cloneNode(true);
        fragment.querySelectorAll("[data-repeater-field]").forEach((element) => {
          const fieldId = element.dataset.repeaterField;
          element.value =
            item?.[fieldId] ||
            (element.dataset.defaultSource === "today"
              ? formatTodayInputValue()
              : element.dataset.defaultValue || "");
          element.disabled = isReviewed || (isRecurringReviewTask && !isAdmin);
        });
        fragment.querySelectorAll("[data-repeater-remove]").forEach((button) => {
          button.disabled = isReviewed || (isRecurringReviewTask && !isAdmin);
        });
        itemsContainer.appendChild(fragment);
      });

      itemsContainer
        .querySelectorAll("[data-repeater-row]")
        .forEach((row, index) => {
          const label = row.querySelector("[data-repeater-row-label]");
          if (label) label.textContent = `Axle ${index + 1}`;
        });

      const addButton = repeater.querySelector("[data-repeater-add]");
      if (addButton) {
        addButton.disabled = isReviewed || (isRecurringReviewTask && !isAdmin);
      }
    });

    syncRequiredFieldIndicators(container, {
      hideRequiredMarkers: isItemRecordTask,
    });
  }, [
    customFieldValues,
    expiryDate,
    isRecurringReviewTask,
    isRecurringRenewalPhase,
    isAdmin,
    isReviewed,
    latestDriverLicense,
    ownerOperatorCompanyProfile,
    optionSourceRows,
    task?.t_content,
    user,
    isItemRecordTask,
  ]);

  // --------------------------------------------------
  // Load Expiry Date (ONLY if enabled)
  // --------------------------------------------------
  useEffect(() => {
    if (
      (!task?.t_show_expiry &&
        !task?.t_show_issue_date &&
        !isRecurringReviewTask) ||
      isNewListRecord ||
      shouldShowListView
    ) {
      return;
    }

    axios
      .get(
        `${API_BASE}/api/custom-tasks/expiry/${task.t_key}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: referenceParams,
        },
      )
      .then((res) => {
        setExpiryDate(formatDateInputValue(res.data?.te_expiry_date) || "");
        if (res.data?.te_issue_date) {
          setIssueDate(formatDateInputValue(res.data.te_issue_date));
        }
      });
  }, [
    task,
    currentReferenceId,
    effectiveUserId,
    API_BASE,
    isRecurringReviewTask,
    isNewListRecord,
    referenceParams,
    shouldShowListView,
    token,
  ]);

  useEffect(() => {
    if (!isRecurringReviewTask || !user) return;
    if (isRecurringRenewalPhase || isRecurringExpiryCleared) {
      return;
    }

    const hireDateDefault = formatDateInputValue(
      customFieldValuesRef.current.date_of_hire ||
        user.user_hiring_date ||
        user.date_hired,
    );

    if (
      hireDateDefault &&
      !customFieldValuesRef.current.date_of_hire &&
      !isNewListRecord
    ) {
      customFieldValuesRef.current = {
        ...customFieldValuesRef.current,
        date_of_hire: hireDateDefault,
      };
      setCustomFieldValues((prev) => ({
        ...prev,
        date_of_hire: prev.date_of_hire || hireDateDefault,
      }));
    }

  }, [
    customFieldValues.date_of_hire,
    isRecurringExpiryCleared,
    expiryDate,
    isRecurringReviewTask,
    isRecurringRenewalPhase,
    isNewListRecord,
    recurringReviewConfig,
    user,
  ]);

  const saveTaskDates = async (override = {}) => {
    if (!task?.t_show_expiry && !task?.t_show_issue_date && !isRecurringReviewTask) {
      return;
    }
    const resolvedReferenceId =
      override.referenceId ?? currentReferenceId;
    if (task?.t_show_list === 1 && resolvedReferenceId === null) return;

    const normalizeDate = (value) => (value ? value : null);
    const resolvedExpiryDate = isRecurringReviewTask && isRecurringExpiryCleared
      ? null : normalizeDate(override.expiryDate ?? expiryDate);
    const resolvedIssueDate = normalizeDate(override.issueDate ?? issueDate);

    if (!resolvedExpiryDate && !resolvedIssueDate) return;

    await axios.post(
      `${API_BASE}/api/custom-tasks/expiry`,
      {
        taskKey: task.t_key,
        userId: effectiveUserId,
        expiryDate: resolvedExpiryDate,
        issueDate: resolvedIssueDate,
        referenceId: resolvedReferenceId,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  };

  const canEditAttachments = task?.t_allow_upload === 1 && !isReviewed;
  const canDeleteSavedAttachments =
    (canEditAttachments && !isReadyForReview) ||
    (isAdmin && isReadyForReview) ||
    canDeleteRecords;

  const handleCreateRecord = useCallback(async () => {
    if (!task?.t_key || !effectiveUserId) return;
    navigate(`/task/${task.t_key}/${effectiveUserId}/new`);
  }, [effectiveUserId, navigate, task]);

  const createRecordReference = useCallback(async (requiredValues = {}) => {
    const res = await axios.post(
      `${API_BASE}/api/custom-tasks/records`,
      {
        taskKey: task.t_key,
        userId: effectiveUserId,
        requiredValues,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    const nextReferenceId = res.data?.referenceId;
    if (!nextReferenceId) {
      throw new Error("Missing custom task record id.");
    }

    return {
      referenceId: String(nextReferenceId),
      reused: Boolean(res.data?.reused),
    };
  }, [API_BASE, effectiveUserId, task, token]);

  const handleCancel = () => {
    if (isSlidePage) {
      navigate(-1);
      return;
    }
    if (isAdmin && paramUserId) {
      navigate(`/userhome/${paramUserId}`);
    } else {
      navigate("/userhome");
    }
  };

  if (!task || !user) return null;

  const employeeName = `${user.first_name} ${user.last_name}`;
  const today = new Date().toLocaleDateString();
  const displayedTaskDate = taskSignedDate
    ? new Date(taskSignedDate).toLocaleDateString()
    : today;
  const todayInputValue = formatTodayInputValue();
  const customFields = getCustomFieldTokens(task.t_content);
  const hasCustomFields = customFields.length > 0;
  const contentPosition =
    task.t_content_position === "top" ? "top" : "bottom";
  const hasTaskFields =
    hasCustomFields ||
    contentMetadata.hasHtmlFields ||
    contentMetadata.hasRepeaters ||
    task.t_show_document_number === 1 ||
    task.t_show_card_number === 1 ||
    task.t_show_issue_country === 1 ||
    task.t_show_nationality === 1;

  const injectTaskVariables = (html) => {
    if (!html) return "";

    return html
      .replace(
        CUSTOM_FIELD_TOKEN_REGEX,
        (match, _fullType, _optionalPrefix, blockPrefix, fieldType, fieldId) => {
          const type =
            fieldType === "date_field"
              ? "date"
              : fieldType === "textarea_field"
                ? "textarea"
                : fieldType === "yesno_field"
                  ? "yesno"
                  : "text";
          const defaultValue = getLockedFieldDefaultValue(
            fieldId,
            user,
            todayInputValue,
          );
          const value = resolveCustomTaskFieldValue(
            customFieldValues[fieldId],
            defaultValue,
            type,
          );
          const isLockedForUser = isLockedFieldForUser(fieldId, isAdmin);
          const disabled =
            isReviewed || isLockedForUser || (isRecurringReviewTask && !isAdmin)
              ? " disabled"
              : "";
          const isRequiredField =
            !_optionalPrefix &&
            (!isRecurringReviewTask || fieldId === "date_of_hire");
          const requiredAttributes = isRequiredField
            ? ' data-required="true" required'
            : "";
          const isBlock = Boolean(blockPrefix);

          if (type === "textarea") {
            return `<textarea class="form-control custom-task-textarea-field" data-custom-task-field="${escapeHtml(
              fieldId,
            )}" data-custom-task-field-type="textarea"${requiredAttributes}${disabled}>${escapeHtml(
              value,
            )}</textarea>`;
          }

          if (type === "yesno") {
            const yesChecked =
              String(value || "").toLowerCase() === "yes" ? " checked" : "";
            const noChecked =
              String(value || "").toLowerCase() === "no" ? " checked" : "";

            return `<span class="custom-task-yesno-field d-inline-flex align-items-center gap-4 flex-wrap" data-custom-task-yesno-group="${escapeHtml(
              fieldId,
            )}">
              <label class="d-inline-flex align-items-center gap-2 mb-0"><span>Yes</span><input type="radio" name="custom-task-${escapeHtml(
                fieldId,
              )}" data-custom-task-field="${escapeHtml(
                fieldId,
              )}" data-custom-task-field-type="yesno" value="yes"${requiredAttributes}${yesChecked}${disabled} /></label>
              <label class="d-inline-flex align-items-center gap-2 mb-0"><span>No</span><input type="radio" name="custom-task-${escapeHtml(
                fieldId,
              )}" data-custom-task-field="${escapeHtml(
                fieldId,
              )}" data-custom-task-field-type="yesno" value="no"${requiredAttributes}${noChecked}${disabled} /></label>
            </span>`;
          }

          const inputClass =
            isBlock
              ? "form-control custom-task-block-field"
              : type === "date"
                ? "custom-task-inline-field custom-task-date-field"
                : "custom-task-inline-field";

          return `<input type="${type}" class="${inputClass}" data-custom-task-field="${escapeHtml(
            fieldId,
          )}" data-custom-task-field-type="${type}" value="${escapeHtml(
            value,
          )}"${requiredAttributes}${disabled} />`;
        },
      )
      .replaceAll("{{employee_name}}", escapeHtml(employeeName))
      .replaceAll("{{task_title}}", escapeHtml(task.t_display_title))
      .replaceAll("{{date}}", escapeHtml(today));
  };

  const formatListDate = (value) => {
    return formatDisplayDate(value);
  };

  const handleDeleteRecord = async (targetRecordId) => {
    if (!canDeleteRecords || !task?.t_key || !effectiveUserId || !targetRecordId) {
      return;
    }

    if (!window.confirm("Delete this record? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/api/custom-tasks/records/${task.t_key}/${effectiveUserId}/${targetRecordId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setRecords((prev) =>
        prev.filter((record) => String(record.record_id) !== String(targetRecordId)),
      );
    } catch (error) {
      console.error("Custom task record delete failed:", error);
      alert(error?.response?.data?.message || "Failed to delete record.");
    }
  };

  if (shouldRenderListView) {
    return (
      <div className="form-wrapper my-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">{task.t_display_title}</h3>
          <div>
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() =>
                navigate(isAdmin && paramUserId ? `/userhome/${paramUserId}` : "/userhome")
              }
            >
              Return
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleCreateRecord}
            >
              Add
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {isLoadingRecords ? (
              <div className="text-muted text-center">
                Loading {task.t_display_title} records...
              </div>
            ) : records.length === 0 ? (
              <div className="text-muted text-center">
                No {task.t_display_title} records found.
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className={`table table-striped table-bordered${
                    contentMetadata.showListAudit
                      ? " custom-task-audit-table"
                      : ""
                  }`}
                >
                  <thead className="table-light">
                    <tr>
                      <th className="custom-task-row-number">#</th>
                      {contentMetadata.listColumns.map((column) => (
                        <th key={column.field}>{column.label}</th>
                      ))}
                      {contentMetadata.showListAudit && (
                        <>
                          <th>Date Saved</th>
                          <th>Saved By</th>
                          <th>Date Created</th>
                          <th>Created By</th>
                          <th>Is Complete</th>
                          <th>Date Completed</th>
                        </>
                      )}
                      {task.t_show_document_number === 1 && (
                        <th>Document Number</th>
                      )}
                      {task.t_show_card_number === 1 && <th>Card Number</th>}
                      {task.t_show_issue_country === 1 && <th>Issue Country</th>}
                      {task.t_show_nationality === 1 && <th>Nationality</th>}
                      {task.t_show_issue_date === 1 && <th>Issue Date</th>}
                      {(task.t_show_expiry === 1 || isRecurringReviewTask) && (
                        <th>{contentMetadata.expiryLabel}</th>
                      )}
                      {!contentMetadata.showListAudit &&
                        task.t_need_complete === 1 && <th>Complete Date</th>}
                      {task.t_allow_upload === 1 &&
                        !contentMetadata.showListAudit && <th>Files</th>}
                      {!contentMetadata.showListAudit &&
                        task.t_need_complete === 1 && <th>Status</th>}
                      <th style={{ width: "110px" }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, index) => (
                      <tr key={record.record_id}>
                        <td>{index + 1}</td>
                        {contentMetadata.listColumns.map((column) => (
                          <td key={`${record.record_id}-${column.field}`}>
                            {formatCustomTaskListColumnValue(record, column)}
                          </td>
                        ))}
                        {contentMetadata.showListAudit && (
                          <>
                            <td>{formatListDate(record.audit?.saved_date)}</td>
                            <td>{record.audit?.saved_by || "-"}</td>
                            <td>{formatListDate(record.audit?.created_date)}</td>
                            <td>{record.audit?.created_by || "-"}</td>
                            <td>{record.status === "completed" ? "Yes" : "No"}</td>
                            <td>
                              {formatListDate(record.completion_date) || "-"}
                            </td>
                          </>
                        )}
                        {task.t_show_document_number === 1 && (
                          <td>{record.document_number || "-"}</td>
                        )}
                        {task.t_show_card_number === 1 && (
                          <td>{record.card_number || "-"}</td>
                        )}
                        {task.t_show_issue_country === 1 && (
                          <td>{record.issue_country || "-"}</td>
                        )}
                        {task.t_show_nationality === 1 && (
                          <td>{record.nationality || "-"}</td>
                        )}
                        {task.t_show_issue_date === 1 && (
                          <td>{formatListDate(record.issue_date)}</td>
                        )}
                        {(task.t_show_expiry === 1 || isRecurringReviewTask) && (
                          <td>{formatListDate(record.expiry_date)}</td>
                        )}
                        {!contentMetadata.showListAudit &&
                          task.t_need_complete === 1 && (
                          <td>
                            {formatListDate(record.completion_date) || "-"}
                          </td>
                        )}
                        {task.t_allow_upload === 1 &&
                          !contentMetadata.showListAudit && (
                          <td>{record.attachment_count || 0}</td>
                        )}
                        {!contentMetadata.showListAudit &&
                          task.t_need_complete === 1 && (
                          <td>
                            {record.status === "expired"
                              ? "Expired"
                              : record.status || "not_completed"}
                          </td>
                        )}
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              type="button"
                              className="app-table-action-button is-edit"
                              onClick={() =>
                                navigate(
                                  `/task/${task.t_key}/${effectiveUserId}/detail/${record.record_id}`,
                                )
                              }
                              title="Edit record"
                              aria-label="Edit record"
                            >
                              <i className="fas fa-edit" aria-hidden="true" />
                            </button>
                            {canDeleteRecords && (
                              <button
                                type="button"
                                className="app-table-action-button is-delete"
                                onClick={() => handleDeleteRecord(record.record_id)}
                                title="Delete record"
                                aria-label="Delete record"
                              >
                                <i className="fas fa-trash" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (shouldHoldFormRenderForAutoOpen) {
    return (
      <div className="form-wrapper my-4">
        <div className="card">
          <div className="card-body text-muted text-center">
            Loading {task?.t_display_title || "task"}...
          </div>
        </div>
      </div>
    );
  }

  const collectCustomFieldValues = ({ requireValues = false } = {}) => {
    const nextValues = {};
    const missingFields = [];
    const fieldDefinitions = customFields || [];

    fieldDefinitions.forEach((field) => {
      let value = "";

      if (field.type === "yesno") {
        const checkedInput = Array.from(
          taskContentRef.current?.querySelectorAll(
            `[data-custom-task-field="${field.id}"]`,
          ) || [],
        ).find((input) => input.checked);
        value = checkedInput?.value || customFieldValuesRef.current[field.id] || "";
      } else {
        const input = taskContentRef.current?.querySelector(
          `[data-custom-task-field="${field.id}"]`,
        );
        value = input?.value || "";
        if (input && isCustomTaskPhoneField(input, field.id)) {
          value = formatPhoneNumber(value);
        }
      }

      if (!value) {
        value = getLockedFieldDefaultValue(field.id, user, todayInputValue);
      }

      nextValues[field.id] = value;

      if (requireValues && field.required && !String(value || "").trim()) {
        missingFields.push(field.id);
      }
    });

    const processedFieldIds = new Set(fieldDefinitions.map((field) => field.id));
    taskContentRef.current
      ?.querySelectorAll("[data-custom-task-field]")
      .forEach((element) => {
        const fieldId = element.dataset.customTaskField;
        if (!fieldId || processedFieldIds.has(fieldId)) return;

        processedFieldIds.add(fieldId);
        let value = "";

        if (element.type === "radio") {
          const checkedInput = Array.from(
            taskContentRef.current?.querySelectorAll(
              `[data-custom-task-field="${fieldId}"]`,
            ) || [],
          ).find((input) => input.checked);
          value = checkedInput?.value || customFieldValuesRef.current[fieldId] || "";
        } else if (element.type === "checkbox") {
          value = element.checked ? "1" : "";
        } else {
          value = element.value || "";
          if (isCustomTaskPhoneField(element, fieldId)) {
            value = formatPhoneNumber(value);
          }
        }

        nextValues[fieldId] = value;

        const conditionalRequiredField = element.dataset.requiredWhenField;
        const conditionalRequiredValue =
          element.dataset.requiredWhenValue || "";
        const conditionalRequiredSource = conditionalRequiredField
          ? taskContentRef.current?.querySelector(
              `[data-custom-task-field="${conditionalRequiredField}"]`,
            )
          : null;
        const isConditionallyRequired =
          Boolean(conditionalRequiredField) &&
          String(conditionalRequiredSource?.value || "") ===
            conditionalRequiredValue;

        if (
          requireValues &&
          (element.dataset.required === "true" || isConditionallyRequired) &&
          !String(value).trim()
        ) {
          missingFields.push(fieldId);
        }
      });

    taskContentRef.current
      ?.querySelectorAll("[data-custom-task-repeater]")
      .forEach((repeater) => {
        const repeaterId = repeater.dataset.customTaskRepeater;
        if (!repeaterId) return;

        nextValues[repeaterId] = Array.from(
          repeater.querySelectorAll("[data-repeater-row]"),
        ).map((row) =>
          Array.from(row.querySelectorAll("[data-repeater-field]")).reduce(
            (item, element) => {
              const fieldId = element.dataset.repeaterField;
              item[fieldId] = isCustomTaskPhoneField(element, fieldId)
                ? formatPhoneNumber(element.value)
                : element.value || "";
              return item;
            },
            {},
          ),
        );
      });

    if (task.t_show_document_number === 1) {
      nextValues.document_number = documentNumber || "";
      delete nextValues.documentNumber;

      if (requireValues && !String(documentNumber || "").trim()) {
        missingFields.push("document_number");
      }
    }

    if (task.t_show_card_number === 1) {
      nextValues.card_number = cardNumber || "";
      delete nextValues.fuel_card_number;

      if (requireValues && !String(cardNumber || "").trim()) {
        missingFields.push("card_number");
      }
    }

    if (task.t_show_issue_country === 1) {
      nextValues.issue_country = issueCountry || "";
      delete nextValues.issueCountry;

      if (requireValues && !String(issueCountry || "").trim()) {
        missingFields.push("issue_country");
      }
    }

    if (task.t_show_nationality === 1) {
      nextValues.nationality = nationality || "";
    }

    if (
      requireValues &&
      task.t_show_expiry === 1 &&
      !isRecurringReviewTask &&
      !String(expiryDate || "").trim()
    ) {
      missingFields.push("expiry_date");
    }

    if (missingFields.length) {
      const fieldLabels = {
        document_number: "Document Number",
        card_number: "Card Number",
        issue_country: "Issue Country",
        expiry_date: contentMetadata.expiryLabel,
      };
      const labels = [...new Set(missingFields)].map(
        (fieldId) => fieldLabels[fieldId] || fieldId.replace(/_/g, " "),
      );
      throw new Error(`Please complete the required fields: ${labels.join(", ")}.`);
    }

    if (task?.t_key === DRIVER_LICENCE_COMPLIANCE_TASK_KEY) {
      return Object.fromEntries(
        Object.entries(nextValues).filter(([fieldId]) =>
          DRIVER_LICENCE_COMPLIANCE_FIELD_IDS.has(fieldId),
        ),
      );
    }

    if (task?.t_key === ROAD_TEST_TASK_KEY) {
      return Object.fromEntries(
        Object.entries(nextValues).filter(([fieldId]) =>
          ROAD_TEST_FIELD_IDS.has(fieldId),
        ),
      );
    }

    return nextValues;
  };

  const saveCustomFieldValues = async ({
    requireValues = false,
    updateState = true,
    referenceId = currentReferenceId,
  } = {}) => {
    if (!hasTaskFields) return;
    if (task?.t_show_list === 1 && referenceId === null) return;

    const fields = collectCustomFieldValues({ requireValues });
    const editVersionAtSave = customFieldEditVersionRef.current;
    const saveRequest = customFieldSaveQueueRef.current
      .catch(() => undefined)
      .then(() =>
        axios.post(
          `${API_BASE}/api/custom-tasks/fields`,
          {
            taskKey: task.t_key,
            userId: effectiveUserId,
            referenceId,
            fields,
            validateRequired: requireValues,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      );

    customFieldSaveQueueRef.current = saveRequest;
    await saveRequest;

    if (
      !shouldApplyCustomTaskSaveResult(
        editVersionAtSave,
        customFieldEditVersionRef.current,
      )
    ) {
      return fields;
    }

    customFieldValuesRef.current = fields;
    if (updateState) {
      setCustomFieldValues(fields);
    }
    return fields;
  };

  const handleCustomFieldInput = (e) => {
    if (e.target?.hasAttribute("data-custom-task-expiry-date")) {
      setExpiryDate(e.target.value || "");
      return;
    }

    const fieldId = e.target?.dataset?.customTaskField;
    const repeaterFieldId = e.target?.dataset?.repeaterField;

    if (fieldId || repeaterFieldId) {
      customFieldEditVersionRef.current += 1;
    }

    if (repeaterFieldId) {
      if (isCustomTaskPhoneField(e.target, repeaterFieldId)) {
        e.target.value = formatPhoneNumber(e.target.value);
      }

      const repeater = e.target.closest("[data-custom-task-repeater]");
      const repeaterId = repeater?.dataset.customTaskRepeater;
      if (!repeaterId) return;

      customFieldValuesRef.current = {
        ...customFieldValuesRef.current,
        [repeaterId]: Array.from(
          repeater.querySelectorAll("[data-repeater-row]"),
        ).map((row) =>
          Array.from(row.querySelectorAll("[data-repeater-field]")).reduce(
            (item, element) => {
              const fieldId = element.dataset.repeaterField;
              item[fieldId] = isCustomTaskPhoneField(element, fieldId)
                ? formatPhoneNumber(element.value)
                : element.value || "";
              return item;
            },
            {},
          ),
        ),
      };
    } else {
      if (!fieldId) return;
      if (e.target.type === "radio" && !e.target.checked) return;

      if (isCustomTaskPhoneField(e.target, fieldId)) {
        e.target.value = formatPhoneNumber(e.target.value);
      }

      const updates = {
        [fieldId]:
          e.target.type === "checkbox"
            ? e.target.checked
              ? "1"
              : ""
            : e.target.value || "",
      };

      const optionSource = e.target.dataset.optionsSource;
      if (optionSource) {
        const selectedOption = getCustomTaskSelectOptions(
          optionSource,
          optionSourceRows[optionSource] || [],
        ).find((option) => option.value === String(e.target.value));

        if (e.target.dataset.storeLabelAs) {
          updates[e.target.dataset.storeLabelAs] = selectedOption?.label || "";
        }

        try {
          const autofill = JSON.parse(e.target.dataset.autofill || "{}");
          Object.entries(autofill).forEach(([targetFieldId, sourceField]) => {
            const value = selectedOption?.record?.[sourceField] || "";
            updates[targetFieldId] = value;
            const target = taskContentRef.current?.querySelector(
              `[data-custom-task-field="${targetFieldId}"]`,
            );
            if (target) target.value = value;
          });
        } catch (error) {
          console.error("Invalid custom task autofill configuration", error);
        }
      }

      customFieldValuesRef.current = {
        ...customFieldValuesRef.current,
        ...updates,
      };

      if (e.target.type === "checkbox") {
        setCustomFieldValues(customFieldValuesRef.current);
      }

      if (fieldId === contentMetadata.requiredAttachmentTypesByField?.field) {
        setCustomFieldValues(customFieldValuesRef.current);
        setSelectedAttachmentType("");
      }

      if (
        fieldId === contentMetadata.embeddedExpiryDefaultField &&
        contentMetadata.embeddedExpiryDefaultSource === "field-plus-one-year"
      ) {
        setExpiryDate(addYearsToDateValue(e.target.value, 1));
      }

      syncConditionalCustomTaskFields(taskContentRef.current);
      syncRequiredFieldIndicators(taskContentRef.current, {
        hideRequiredMarkers: isItemRecordTask,
      });
    }

    if (isNewListRecord || isRecurringReviewTask) {
      return;
    }

    if (customFieldSaveTimerRef.current) {
      clearTimeout(customFieldSaveTimerRef.current);
    }

    const saveDelay = e.target.type === "checkbox" ? 0 : 700;
    customFieldSaveTimerRef.current = setTimeout(() => {
      saveCustomFieldValues({ updateState: false }).catch((err) => {
        console.error("Failed to auto-save custom fields", err);
      });
    }, saveDelay);
  };

  const handleCustomTaskContentClick = (e) => {
    const addButton = e.target.closest("[data-repeater-add]");
    const removeButton = e.target.closest("[data-repeater-remove]");
    if (!addButton && !removeButton) return;

    const repeater = e.target.closest("[data-custom-task-repeater]");
    const repeaterId = repeater?.dataset.customTaskRepeater;
    const itemsContainer = repeater?.querySelector("[data-repeater-items]");
    if (!repeater || !repeaterId || !itemsContainer) return;

    customFieldEditVersionRef.current += 1;

    if (addButton) {
      const template = repeater.querySelector("template[data-repeater-template]");
      if (!template) return;

      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll("[data-repeater-field]").forEach((element) => {
        element.value =
          element.dataset.defaultSource === "today"
            ? formatTodayInputValue()
            : element.dataset.defaultValue || "";
      });
      itemsContainer.appendChild(fragment);
    }

    if (removeButton) {
      const rowToRemove =
        removeButton.closest("[data-repeater-row]") ||
        itemsContainer.querySelector("[data-repeater-row]:last-child");
      rowToRemove?.remove();
    }

    itemsContainer
      .querySelectorAll("[data-repeater-row]")
      .forEach((row, index) => {
        const label = row.querySelector("[data-repeater-row-label]");
        if (label) label.textContent = `Axle ${index + 1}`;
      });

    customFieldValuesRef.current = {
      ...customFieldValuesRef.current,
      [repeaterId]: Array.from(
        repeater.querySelectorAll("[data-repeater-row]"),
      ).map((row) =>
        Array.from(row.querySelectorAll("[data-repeater-field]")).reduce(
          (item, element) => {
            const fieldId = element.dataset.repeaterField;
            item[fieldId] = isCustomTaskPhoneField(element, fieldId)
              ? formatPhoneNumber(element.value)
              : element.value || "";
            return item;
          },
          {},
        ),
      ),
    };

    if (!isNewListRecord && !isRecurringReviewTask) {
      if (customFieldSaveTimerRef.current) {
        clearTimeout(customFieldSaveTimerRef.current);
        customFieldSaveTimerRef.current = null;
      }

      saveCustomFieldValues({ updateState: false }).catch((err) => {
        console.error("Failed to auto-save repeated custom fields", err);
      });
    }
  };

  const handleCustomTaskContentMouseDown = (e) => {
    if (e.target.closest("[data-repeater-add], [data-repeater-remove]")) {
      e.preventDefault();
    }
  };

  const handleCustomFieldBlur = (e) => {
    if (e.target?.hasAttribute("data-custom-task-expiry-date")) {
      saveTaskDates({ expiryDate: e.target.value }).catch((err) => {
        console.error("Failed to save embedded expiry date", err);
      });
      return;
    }

    if (isNewListRecord || isRecurringReviewTask) return;

    if (customFieldSaveTimerRef.current) {
      clearTimeout(customFieldSaveTimerRef.current);
      customFieldSaveTimerRef.current = null;
    }

    saveCustomFieldValues().catch((err) => {
      console.error("Failed to auto-save custom fields", err);
    });
  };

  const handleCardNumberBlur = () => {
    if (isNewListRecord || isRecurringReviewTask) return;

    saveCustomFieldValues().catch((err) => {
      console.error("Failed to auto-save card number", err);
    });
  };

  const queueAttachmentFiles = (files, attachmentType) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;

    if (!canEditAttachments) return;

    const normalizedAttachmentType = String(attachmentType || "").trim();
    if (contentMetadata.attachmentTypes.length && !normalizedAttachmentType) {
      alert("Please select an attachment type.");
      return;
    }

    setPendingFiles((prev) => {
      const existingKeys = new Set(
        prev.map(
          (entry) =>
            `${entry.file.name}_${entry.file.size}_${entry.file.lastModified}_${entry.attachmentType}`,
        ),
      );
      const next = [...prev];

      for (const file of selected) {
        const key = `${file.name}_${file.size}_${file.lastModified}_${normalizedAttachmentType}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push({ file, attachmentType: normalizedAttachmentType });
        }
      }

      return next;
    });

    if (contentMetadata.attachmentTypes.length) {
      setSelectedAttachmentType("");
    }
  };

  const handleSelectFiles = (e) => {
    queueAttachmentFiles(
      e.target.files,
      e.target.dataset.customTaskAttachmentType || selectedAttachmentType,
    );
    e.target.value = "";
  };

  const uploadVehicleAttachmentFiles = async (files, attachmentType) => {
    const selectedFiles = Array.from(files || []);
    const normalizedAttachmentType = String(attachmentType || "").trim();
    if (
      !selectedFiles.length ||
      !normalizedAttachmentType ||
      !canEditAttachments ||
      vehicleUploadInFlightRef.current
    ) {
      return;
    }

    const entries = selectedFiles.map((file) => ({
      file,
      attachmentType: normalizedAttachmentType,
    }));
    const entryKeys = new Set(
      entries.map(
        ({ file }) =>
          `${file.name}_${file.size}_${file.lastModified}_${normalizedAttachmentType}`,
      ),
    );

    setPendingFiles((previous) => {
      const existingKeys = new Set(
        previous.map(
          ({ file, attachmentType: type }) =>
            `${file.name}_${file.size}_${file.lastModified}_${type}`,
        ),
      );
      return [
        ...previous,
        ...entries.filter(
          ({ file }) =>
            !existingKeys.has(
              `${file.name}_${file.size}_${file.lastModified}_${normalizedAttachmentType}`,
            ),
        ),
      ];
    });

    vehicleUploadInFlightRef.current = true;
    setIsSavingFiles(true);

    try {
      let resolvedReferenceId = currentReferenceId;
      if (task?.t_show_list === 1 && resolvedReferenceId === null) {
        const recordResult = await createRecordReference({});
        resolvedReferenceId = recordResult.referenceId;
        await saveCustomFieldValues({
          referenceId: resolvedReferenceId,
          updateState: false,
        });
      }

      const formData = new FormData();
      entries.forEach(({ file, attachmentType: type }) => {
        formData.append("files", file);
        formData.append("attachmentTypes", type);
      });
      formData.append("userId", effectiveUserId);
      formData.append("taskKey", task.t_key);
      formData.append("categoryId", task.t_category_id);
      formData.append("staged", "true");
      if (resolvedReferenceId !== null) {
        formData.append("referenceId", resolvedReferenceId);
      }

      const uploadResponse = await axios.post(
        `${API_BASE}/api/custom-tasks/upload-attachment?taskKey=${encodeURIComponent(
          task.t_key,
        )}&userId=${effectiveUserId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      const savedReferenceId = String(
        uploadResponse.data?.referenceId ?? resolvedReferenceId,
      );
      const refreshed = await axios.get(
        `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
          task.t_key,
        )}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { referenceId: savedReferenceId },
        },
      );

      setUploadedFiles(Array.isArray(refreshed.data) ? refreshed.data : []);
      setPendingFiles((previous) =>
        previous.filter(
          ({ file, attachmentType: type }) =>
            !entryKeys.has(`${file.name}_${file.size}_${file.lastModified}_${type}`),
        ),
      );

      if (savedReferenceId !== String(currentReferenceId || "")) {
        navigate(
          `/task/${task.t_key}/${effectiveUserId}/detail/${savedReferenceId}`,
          { replace: true },
        );
      }
    } catch (error) {
      console.error("Vehicle attachment upload failed", error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload the vehicle attachment.",
      );
    } finally {
      vehicleUploadInFlightRef.current = false;
      setIsSavingFiles(false);
    }
  };

  const removePendingFile = (entryToRemove) => {
    setPendingFiles((prev) =>
      prev.filter((entry) => entry !== entryToRemove),
    );
  };

  const savePendingAttachments = async (referenceId) => {
    if (!pendingFiles.length) return referenceId;

    const formData = new FormData();
    pendingFiles.forEach(({ file, attachmentType }) => {
      formData.append("files", file);
      formData.append("attachmentTypes", attachmentType);
    });
    formData.append("userId", effectiveUserId);
    formData.append("taskKey", task.t_key);
    formData.append("categoryId", task.t_category_id);
    if (referenceId !== null) {
      formData.append("referenceId", referenceId);
    }

    const uploadResponse = await axios.post(
      `${API_BASE}/api/custom-tasks/upload-attachment?taskKey=${encodeURIComponent(
        task.t_key,
      )}&userId=${effectiveUserId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    const savedReferenceId =
      uploadResponse.data?.referenceId ?? referenceId;
    const refreshed = await axios.get(
      `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
        task.t_key,
      )}/${effectiveUserId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params:
          savedReferenceId !== null
            ? { referenceId: savedReferenceId }
            : {},
      },
    );
    if (Array.isArray(refreshed.data)) {
      setUploadedFiles(refreshed.data);
    }
    setPendingFiles([]);
    return savedReferenceId;
  };

  const handleSaveAttachments = async (e) => {
    e?.preventDefault?.();
    let createdRecordReferenceId = null;

    try {
      setIsSavingFiles(true);
      let resolvedReferenceId = currentReferenceId;

      const uploadedAttachmentTypes = new Set(
        [
          ...uploadedFiles.map((file) => file.ta_type),
          ...pendingFiles.map((entry) => entry.attachmentType),
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      );
      const attachmentRule = contentMetadata.requiredAttachmentTypesByField;
      const attachmentRuleSourceValue = String(
        customFieldValuesRef.current?.[attachmentRule?.field] ||
          taskContentRef.current?.querySelector(
            `[data-custom-task-field="${attachmentRule?.field || ""}"]`,
          )?.value ||
          "",
      );
      const conditionalRequiredAttachmentTypes =
        attachmentRule?.values?.[attachmentRuleSourceValue];
      const requiredAttachmentTypes = Array.isArray(
        conditionalRequiredAttachmentTypes,
      )
        ? conditionalRequiredAttachmentTypes
        : contentMetadata.requiredAttachmentTypes;
      const missingAttachmentTypes = requiredAttachmentTypes.filter(
        (attachmentType) => !uploadedAttachmentTypes.has(attachmentType),
      );
      if (missingAttachmentTypes.length) {
        throw new Error(
          `Please upload the required file${
            missingAttachmentTypes.length === 1 ? "" : "s"
          }: ${missingAttachmentTypes.join(", ")}.`,
        );
      }

      collectCustomFieldValues({ requireValues: true });

      if (task?.t_show_list === 1 && resolvedReferenceId === null) {
        const recordResult = await createRecordReference({
          documentNumber,
          issueCountry,
          expiryDate,
        });
        resolvedReferenceId = recordResult.referenceId;
        if (!recordResult.reused) {
          createdRecordReferenceId = resolvedReferenceId;
        }
      }

      await saveTaskDates({ referenceId: resolvedReferenceId });
      await saveCustomFieldValues({
        requireValues: true,
        referenceId: resolvedReferenceId,
      });

      resolvedReferenceId =
        await savePendingAttachments(resolvedReferenceId);

      if (task.t_key === VEHICLE_TASK_KEY && resolvedReferenceId !== null) {
        await axios.post(
          `${API_BASE}/api/custom-tasks/attachments/promote-pending`,
          {
            taskKey: task.t_key,
            userId: effectiveUserId,
            referenceId: resolvedReferenceId,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const refreshedAttachments = await axios.get(
          `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
            task.t_key,
          )}/${effectiveUserId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { referenceId: resolvedReferenceId },
          },
        );
        setUploadedFiles(
          Array.isArray(refreshedAttachments.data)
            ? refreshedAttachments.data
            : [],
        );
      }

      if (task.t_need_complete === 1 && task.t_show_signature !== 1) {
        await axios.post(
          `${API_BASE}/api/usertasks/submit`,
          {
            userId: effectiveUserId,
            taskId: task.t_id,
            taskName: task.t_key,
            referenceId: resolvedReferenceId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            params:
              resolvedReferenceId !== null
                ? { referenceId: resolvedReferenceId }
                : {},
          },
        );
        await refreshTaskStatus(resolvedReferenceId);
      }

      if (resolvedReferenceId !== currentReferenceId) {
        navigate(
          `/task/${task.t_key}/${effectiveUserId}/detail/${resolvedReferenceId}`,
          { replace: true },
        );
      }

      await refreshTaskStatus(resolvedReferenceId);

      alert("Saved successfully");
    } catch (err) {
      console.error("Upload failed", err);
      if (createdRecordReferenceId) {
        try {
          await axios.delete(
            `${API_BASE}/api/custom-tasks/records/${encodeURIComponent(
              task.t_key,
            )}/${effectiveUserId}/${createdRecordReferenceId}/discard`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (cleanupError) {
          console.error("Failed to discard incomplete task record", cleanupError);
        }
      }
      const message =
        err.response?.data?.message || err.message || "Failed to save";
      alert(message);
    } finally {
      setIsSavingFiles(false);
    }
  };

  const handleDeleteSavedAttachment = async (attachment) => {
    const confirmed = window.confirm(
      `Delete uploaded file "${attachment.ta_original_name}"?`,
    );
    if (!confirmed) return;

    try {
      setIsDeletingAttachmentId(attachment.ta_id);
      await axios.delete(`${API_BASE}/api/custom-tasks/attachments/${attachment.ta_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadedFiles((prev) =>
        prev.filter((file) => file.ta_id !== attachment.ta_id),
      );
      await refreshTaskStatus();
    } catch (err) {
      console.error("Delete failed", err);
      const message =
        err.response?.data?.message || "Failed to delete attachment";
      alert(message);
    } finally {
      setIsDeletingAttachmentId(null);
    }
  };

  const handleSaveRecurringReview = async ({
    silent = false,
    navigateAfterSave = true,
  } = {}) => {
    if (!isRecurringReviewTask) return;
    let createdRecordReferenceId = null;

    try {
      setIsSavingFiles(true);
      let resolvedReferenceId = currentReferenceId;
      const nextFields = collectCustomFieldValues({ requireValues: false });
      const resolvedHireDate = formatDateInputValue(
        nextFields.date_of_hire || user?.user_hiring_date || user?.date_hired,
      );

      if (!resolvedHireDate) {
        throw new Error("Date of Hire is required before saving.");
      }

      const nextExpiryDate = isRecurringExpiryCleared || isRecurringRenewalPhase ? null : (expiryDate || null);

      const requiredExpiryDate = nextExpiryDate || expiryDate;

      if (task?.t_show_list === 1 && resolvedReferenceId === null) {
        const recordResult = await createRecordReference({
          expiryDate: requiredExpiryDate,
        });
        resolvedReferenceId = recordResult.referenceId;
        if (!recordResult.reused) {
          createdRecordReferenceId = resolvedReferenceId;
        }
      }

      const workflowFields = {
        ...nextFields,
        ...(isRecurringExpiryCleared && { expiry_date: "", expiry_cleared: true }),
        date_of_hire: resolvedHireDate,
        ...(!isRecurringRenewalPhase && nextExpiryDate
          ? {
              expiry_date: nextExpiryDate,
              initial_expiry_date:
                formatDateInputValue(nextFields.initial_expiry_date) ||
                nextExpiryDate,
            }
          : {}),
        [recurringReviewConfig.phaseField]: isRecurringRenewalPhase
          ? DRIVER_CHECK_IN_RENEWAL_PHASE
          : "initial",
      };

      if (!isRecurringRenewalPhase) {
        setExpiryDate(nextExpiryDate || "");
      }

      const fieldsSaveResponse = await axios.post(
        `${API_BASE}/api/custom-tasks/fields`,
        {
          taskKey: task.t_key,
          userId: effectiveUserId,
          referenceId: resolvedReferenceId,
          fields: workflowFields,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      resolvedReferenceId =
        fieldsSaveResponse.data?.referenceId ?? resolvedReferenceId;

      customFieldValuesRef.current = workflowFields;
      setCustomFieldValues((prev) => ({
        ...prev,
        ...workflowFields,
      }));

      if (!isRecurringRenewalPhase && nextExpiryDate) {
        await saveTaskDates({
          referenceId: resolvedReferenceId,
          expiryDate: nextExpiryDate,
        });
      }

      resolvedReferenceId =
        await savePendingAttachments(resolvedReferenceId);
      await refreshTaskStatus(resolvedReferenceId);
      if (navigateAfterSave && resolvedReferenceId !== currentReferenceId) {
        navigate(
          `/task/${task.t_key}/${effectiveUserId}/detail/${resolvedReferenceId}`,
          { replace: true },
        );
      }
      if (!silent) {
        alert(
          isRecurringRenewalPhase
            ? "Saved successfully. The employee can sign this task now."
            : "Saved successfully.",
        );
      }
      return { referenceId: resolvedReferenceId };
    } catch (err) {
      console.error("Failed to save recurring review", err);
      if (createdRecordReferenceId) {
        try {
          await axios.delete(
            `${API_BASE}/api/custom-tasks/records/${encodeURIComponent(
              task.t_key,
            )}/${effectiveUserId}/${createdRecordReferenceId}/discard`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (cleanupError) {
          console.error("Failed to discard incomplete task record", cleanupError);
        }
      }
      if (silent) throw err;
      alert(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setIsSavingFiles(false);
    }
  };

  const renderedTaskContent = task.t_content ? (
    <div
      ref={taskContentRef}
      className={`custom-task-content ${
        task.t_key === CANADA_POST_CLEARANCE_TASK_KEY
          ? "custom-task-content--canada-post "
          : ""
      }${
        task.t_pdf && contentPosition !== "top" ? "mt-4" : ""
      }`}
      onInput={handleCustomFieldInput}
      onChange={(event) => {
        if (event.target.matches("[data-custom-task-file-input]")) {
          handleSelectFiles(event);
          return;
        }
        handleCustomFieldInput(event);
      }}
      onMouseDown={handleCustomTaskContentMouseDown}
      onClick={handleCustomTaskContentClick}
      onBlur={handleCustomFieldBlur}
      dangerouslySetInnerHTML={{
        __html: injectTaskVariables(task.t_content),
      }}
    />
  ) : null;

  const renderedTaskPdf = task.t_pdf ? (
    <iframe
      src={taskPdfUrl}
      width="100%"
      height="800px"
      style={{ border: "none" }}
      title="Task PDF"
    />
  ) : null;

  const renderedInlineAttachmentStatuses = inlineAttachmentHosts.map(
    ({ element, attachmentType, multiple }) =>
      createPortal(
        <VehicleAttachmentPanel
          apiBase={API_BASE}
          attachmentType={attachmentType}
          multiple={multiple}
          pendingFiles={pendingFiles}
          uploadedFiles={uploadedFiles}
          canEdit={canEditAttachments}
          canDeleteSavedAttachments={canDeleteSavedAttachments}
          isSaving={isSavingFiles}
          deletingFileId={isDeletingAttachmentId}
          onQueueFiles={uploadVehicleAttachmentFiles}
          onRemovePendingFile={removePendingFile}
          onDeleteSavedAttachment={handleDeleteSavedAttachment}
        />,
        element,
        attachmentType,
      ),
  );

  const usesStandardExpiryDate =
    task.t_show_expiry === 1 &&
    !isRecurringReviewTask;
  const hasStandaloneTaskDateFields =
    usesStandardExpiryDate || isRecurringReviewTask;
  const shouldShowStandaloneSaveButton =
    usesStandardExpiryDate ||
    (hasTaskFields && !isRecurringReviewTask) ||
    (isRecurringReviewTask && isAdmin && !isReviewed);
  const renderedStandardExpiryField = usesStandardExpiryDate ? (
    <div className="w-100" style={{ maxWidth: "320px" }}>
      <label className="form-label">
        {contentMetadata.expiryLabel || "Expiry Date"}{" "}
        <span className="text-danger">*</span>
      </label>
      <input
        type="date"
        className="form-control"
        value={expiryDate}
        disabled={isReviewed}
        onChange={(e) => setExpiryDate(e.target.value)}
        onBlur={(e) => saveTaskDates({ expiryDate: e.target.value })}
        required
      />
    </div>
  ) : null;
  const handleClearRecurringExpiry = async () => {
    if (!isSuperAdmin) return;
    try {
      setIsSavingFiles(true);
      if (customFieldSaveTimerRef.current) clearTimeout(customFieldSaveTimerRef.current);
      await customFieldSaveQueueRef.current;
      if (!isReviewed) await saveCustomFieldValues({ updateState: false });
      const response = await axios.post(`${API_BASE}/api/custom-tasks/expiry/clear`, {
        taskKey: task.t_key, userId: effectiveUserId, referenceId: currentReferenceId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      customFieldValuesRef.current = { ...customFieldValuesRef.current, ...response.data.fields };
      setCustomFieldValues(prev => ({ ...prev, ...response.data.fields }));
      setExpiryDate("");
      await refreshTaskStatus();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to clear expiry date");
    } finally {
      setIsSavingFiles(false);
    }
  };
  const renderedRecurringExpiryField = isRecurringReviewTask ? (
    <div className="w-100" style={{ maxWidth: "420px" }}>
      <label className="form-label">Expiry Date</label>
      <div className="d-flex gap-2">
        <input type="date" className="form-control" value={isRecurringExpiryCleared ? "" : expiryDate}
          disabled={isReviewed || !isAdmin || isRecurringRenewalPhase || isRecurringExpiryCleared}
          onChange={(e) => { if (e.target.value) setExpiryDate(e.target.value); }} />
        {isSuperAdmin && <button type="button" className="btn btn-outline-secondary"
          disabled={isSavingFiles || !expiryDate || isRecurringExpiryCleared}
          onClick={handleClearRecurringExpiry}>Clear</button>}
      </div>
      {isRecurringExpiryCleared && customFieldValues.archived_expiry_date && (
        <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
          Reference expiry date: {formatDateInputValue(customFieldValues.archived_expiry_date)}
        </small>
      )}
    </div>
  ) : null;

  return (
    <>
      {/* 🔝 TOP BAR */}
      <div className="d-flex justify-content-between align-items-center pt-3 px-3">
        {!isSlidePage && (
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            {task.t_display_title}
          </span>
        )}

        <div className="d-flex gap-2 ms-auto">
          {task.t_show_list === 1 && !isSlidePage && (
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() =>
                navigate(`/task/${task.t_key}/${effectiveUserId}?view=list`)
              }
              >
                View List
              </button>
          )}
          {isSlidePage && (isBackgroundCheckConsent || isPspDisclosure) && (
            <DocumentDownloadButton
              targetRef={documentRef}
              filename={
                isPspDisclosure
                  ? `psp-disclosure-and-authorization-${effectiveUserId}`
                  : `background-check-consent-${effectiveUserId}`
              }
              label="Download"
              marginMm={15}
              capturePaddingPx={0}
              preservedMediaFrame={{ width: 400, height: 171, padding: 6 }}
              footerText={
                isPspDisclosure
                  ? "New Way Trucking | PSP Disclosure & Authorization"
                  : "New Way Trucking | Consent to Background Checks"
              }
              onClone={(_document, clonedTarget) =>
                clonedTarget.classList.add("consent-document-export")
              }
            />
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            {isSlidePage ? "Close" : "Return"}
          </button>
        </div>
      </div>

      {/* 📄 TASK BODY */}
      <div
        ref={documentRef}
        className={`form-wrapper my-4 print-area ${isSlidePage ? "slide-narrow" : ""}`}
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        {isSlidePage && (
          <div
            className="d-flex justify-content-between align-items-center px-3"
            style={{
              paddingTop: "16px",
              paddingBottom: "12px",
              marginBottom: "16px",
              borderBottom: "1px solid #ccc",
            }}
          >
            <h4
              className="m-0"
              style={{ fontSize: 18, fontWeight: 500, color: "#333" }}
            >
              Review and Sign Consent – {task.t_display_title}
            </h4>
          </div>
        )}

        {/* HEADER */}
        {!isSlidePage && (
          <div className="row align-items-center mb-4 custom-task-document-header">
            <div className="col-3">
              <img src={logo} alt="Logo" style={{ width: "150px" }} />
            </div>

            <div className="col-6 text-center">
              <h2 className="m-0">{task.t_display_title}</h2>
            </div>

            <CompanyAddress />
          </div>
        )}

        {/* TASK CONTENT */}
        {contentPosition === "top" && renderedTaskContent}
        {renderedTaskPdf}
        {contentPosition !== "top" && renderedTaskContent}
        {renderedInlineAttachmentStatuses}

        {/* CARD NUMBER + TASK DATES */}
        {(task.t_show_document_number === 1 ||
          task.t_show_card_number === 1 ||
          task.t_show_issue_date === 1 ||
          task.t_show_issue_country === 1 ||
          task.t_show_nationality === 1) && (
          <div className="row mb-4">
            {task.t_show_document_number === 1 && (
              <div className="col-md-4">
                <label className="form-label">
                  Document Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={documentNumber}
                  disabled={isReviewed}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  onBlur={handleCardNumberBlur}
                  required
                />
              </div>
            )}

            {task.t_show_card_number === 1 && (
              <div className="col-md-4">
                <label className="form-label">Card Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={cardNumber}
                  disabled={isReviewed}
                  onChange={(e) => setCardNumber(e.target.value)}
                  onBlur={handleCardNumberBlur}
                />
              </div>
            )}

            {task.t_show_issue_country === 1 && (
              <div className="col-md-4">
                <label className="form-label">
                  Issue Country <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={issueCountry}
                  disabled={isReviewed}
                  onChange={(e) => setIssueCountry(e.target.value)}
                  onBlur={handleCardNumberBlur}
                  required
                >
                  <option value="">Select country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.country_name}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {task.t_show_nationality === 1 && (
              <div className="col-md-4">
                <label className="form-label">Nationality</label>
                <select
                  className="form-control"
                  value={nationality}
                  disabled={isReviewed}
                  onChange={(e) => setNationality(e.target.value)}
                  onBlur={handleCardNumberBlur}
                >
                  <option value="">Select nationality</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.country_name}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {task.t_show_issue_date === 1 && (
              <div className="col-md-4">
                <label className="form-label">Issue Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={issueDate}
                  disabled={isReviewed}
                  onChange={(e) => setIssueDate(e.target.value)}
                  onBlur={(e) =>
                    saveTaskDates({ issueDate: e.target.value })
                  }
                />
              </div>
            )}

          </div>
        )}

        {/* EMPLOYEE NAME + DATE */}
        {(task.t_show_employee === 1 || task.t_show_date === 1) && (
          <div
            className={`row mb-4 ${
              task.t_key === CANADA_POST_CLEARANCE_TASK_KEY ? "mt-4" : ""
            }`}
          >
            {task.t_show_employee === 1 && (
              <div className="col-md-6">
                <label className="form-label">Employee Name</label>
                <input className="form-control" value={employeeName} readOnly />
              </div>
            )}

            {task.t_show_date === 1 && (
              <div className="col-md-6">
                <label className="form-label">Date</label>
                <input className="form-control" value={displayedTaskDate} readOnly />
              </div>
            )}
          </div>
        )}

        {/* ==================================================
            Upload Section (ONLY if enabled)
           ================================================== */}
        {task.t_allow_upload === 1 && (
          <CustomTaskAttachmentSection
            apiBase={API_BASE}
            attachmentTypes={effectiveAttachmentTypes}
            hideFilePicker={task.t_key === VEHICLE_TASK_KEY}
            hideAttachmentSummary={task.t_key === VEHICLE_TASK_KEY}
            hideSaveDivider={task.t_key === VEHICLE_TASK_KEY}
            selectedAttachmentType={selectedAttachmentType}
            onAttachmentTypeChange={setSelectedAttachmentType}
            pendingFiles={pendingFiles}
            uploadedFiles={uploadedFiles}
            canEdit={canEditAttachments}
            canDeleteSavedAttachments={canDeleteSavedAttachments}
            isSaving={isSavingFiles}
            isReviewed={isReviewed}
            deletingFileId={isDeletingAttachmentId}
            onSelectFiles={handleSelectFiles}
            onRemovePendingFile={removePendingFile}
            onDeleteSavedAttachment={handleDeleteSavedAttachment}
            onSave={
              isRecurringReviewTask
                ? () => handleSaveRecurringReview()
                : handleSaveAttachments
            }
            showSaveButton={
              !isRecurringReviewTask || (isAdmin && !isReviewed)
            }
            beforeSaveContent={
              usesStandardExpiryDate || isRecurringReviewTask ? (
                <div className="d-flex justify-content-end">
                  {usesStandardExpiryDate
                    ? renderedStandardExpiryField
                    : renderedRecurringExpiryField}
                </div>
              ) : null
            }
          />
        )}

        {task.t_allow_upload !== 1 &&
          (hasStandaloneTaskDateFields || shouldShowStandaloneSaveButton) && (
          <>
            {hasStandaloneTaskDateFields && (
              <>
                <hr className="my-4" />
                <div className="d-flex justify-content-end">
                  {usesStandardExpiryDate
                    ? renderedStandardExpiryField
                    : renderedRecurringExpiryField}
                </div>
              </>
            )}
            {shouldShowStandaloneSaveButton && (
              <div className="d-flex justify-content-end mt-3 mb-4">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={
                    isRecurringReviewTask
                      ? () => handleSaveRecurringReview()
                      : handleSaveAttachments
                  }
                  disabled={
                    isSavingFiles ||
                    isReviewed ||
                    (isRecurringReviewTask && !isAdmin)
                  }
                >
                  {isSavingFiles ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </>
        )}

        {task.t_key !== VEHICLE_TASK_KEY && <hr className="my-5" />}

        {/* SIGNATURE + REVIEW (UNCHANGED) */}
        <div className="task-signatures-row">
        {task.t_need_complete === 1 &&
          ((task.t_show_signature === 1 && !isRecurringReviewTask) ||
            (isRecurringReviewTask && shouldShowRecurringEmployeeSignature)) && (
          <SignatureManager
            taskId={task.t_id}
            taskName={task.t_key}
            referenceId={currentReferenceId}
            strictReference={task?.t_show_list === 1}
            userId={effectiveUserId}
            isAdmin={isAdmin}
            showLockNotice={false}
            refreshTrigger={taskStatusValue}
            requireStoredTaskSignature={isRecurringReviewTask && !isReviewed}
            extraAnswers={{
              recurringExpiryDate: hasReachedRecurringExpiry ? expiryDate : null,
            }}
            onBeforeSign={async () => {
              if (isRecurringRenewalPhase || !isReviewed) {
                if (!isRecurringRenewalPhase) {
                  await saveTaskDates();
                }
                await saveCustomFieldValues({
                  requireValues: !isRecurringReviewTask,
                });
              }
            }}
          />
        )}

        {task.t_need_complete === 1 &&
          task.t_requires_supervisor_signature === 1 &&
          (!isRecurringReviewTask || shouldShowRecurringSignature) &&
          (
            <SupervisorSignatureManager
              taskId={task.t_id}
              taskName={task.t_key}
              referenceId={currentReferenceId}
              strictReference={task?.t_show_list === 1}
              userId={effectiveUserId}
              refreshTrigger={taskStatusValue}
              canSign={isAdmin}
              onSignatureChange={setHasSupervisorSignature}
            />
          )}
        </div>

        {task.t_need_complete === 1 && (
          <ReviewManager
            taskId={task.t_id}
            taskName={task.t_key}
            referenceId={currentReferenceId}
            strictReference={task?.t_show_list === 1}
            userId={effectiveUserId}
            isAdmin={isAdmin}
            onBeforeReview={
              isRecurringReviewTask
                ? () =>
                    handleSaveRecurringReview({
                      silent: true,
                      navigateAfterSave: false,
                    })
                : undefined
            }
            onReviewed={refreshTaskStatus}
            refreshTrigger={taskStatusValue}
            showLockNotice={false}
          />
        )}
      </div>
    </>
  );
};

export default CustomTaskPage;
